import axios, { AxiosError } from 'axios';

import store from 'store/store';
import {
	setDeniedAccessErrorOpen,
	setQueueDelayNotificationOpen,
} from 'store/user-service/userSlice';

import { VaultService } from 'utils/storage';

import { ChatGPTModels } from 'core/enums/common';

import { baseURL, refreshAccessToken } from './API';
import { setRequestInterceptorWithToken } from './interceptorHelpers';

// 30 seconds timeout
export const timeBeforeRequestRetry = 30000;

const storage = new VaultService();

const user: any = storage.getItem('user');
const refreshToken = user && user.refreshToken;

const axiosConfig: Record<string, any> = {
	headers: { 'Content-Type': 'application/json' },
	baseURL,
	refreshToken,
};

const chatGptInstance = axios.create(axiosConfig);

// Interceptors
setRequestInterceptorWithToken(chatGptInstance);

chatGptInstance.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		if (error && error.response) {
			switch (error.response.status) {
				case 400:
					if (error.config && error.config.data) {
						const data = JSON.parse(error.config.data);
						if (data?.aiModelEnum) {
							data.aiModelEnum = ChatGPTModels['GPT4-turbo'];
							error.config.data = JSON.stringify(data);
							return chatGptInstance(error.config);
						}
					}
					break;
				case 401:
					// Refresh the token and retry the request
					try {
						if (storage.getItem('token')) {
							await refreshAccessToken();

							if (error.config) return chatGptInstance(error.config);
						} else {
							return Promise.reject(error.response);
						}
					} catch (refreshError) {
						const axiosError = refreshError as AxiosError;
						return Promise.reject(axiosError.response);
					}
					return Promise.reject(error.response);
				case 403:
					store.dispatch(setDeniedAccessErrorOpen(true));
					break;
				case 429:
					if (storage.getItem('token')) {
						store.dispatch(setQueueDelayNotificationOpen(true));
						await new Promise((resolve) =>
							setTimeout(
								resolve,
								error?.response?.headers?.['retry-after']
									? (Number(error.response.headers['retry-after']) + 1) * 1000
									: timeBeforeRequestRetry
							)
						);
						if (error.config) return chatGptInstance(error.config);
					}
					break;
				case 500:
					if (error.config) {
						const retryCount = Number(error?.config?.headers?.['retry-count']);
						if (retryCount >= 5) {
							return Promise.reject(error.response);
						}

						const failedRequest = {
							...error.config,
							headers: {
								...error.config?.headers,
								'retry-count': retryCount + 1,
							},
						};

						return chatGptInstance(failedRequest);
					}

					return Promise.reject(error.response);

				default:
					return Promise.reject(error.response);
			}
		} else {
			return Promise.reject(error.response);
		}

		return;
	}
);

export default chatGptInstance;
